import React from "react";
import { Box, Typography, Grid2, LinearProgress } from "@mui/material";
import { Warning } from "@mui/icons-material";
import { StyledOverflowLinearProgress } from "./OverflowLinearProgress.partial";

interface ProgressBarProps {
  title?: string;
  max: number;
  value: number;
  maxLabel?: string; // Label for the "max" value
  valueLabel?: string; // Label for the "value" progress
  bottomLableFontSize?: string;
}

export const LabeledLinearProgress: React.FC<ProgressBarProps> = ({
  title,
  max,
  value,
  maxLabel,
  valueLabel,
  bottomLableFontSize = "0.75rem"
}) => {
  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      {/* Labels above the progress bar */}
      {title && (
        <Grid2 container justifyContent="center">
          <Typography sx={{ fontSize: "0.85rem" }}>{title}</Typography>
        </Grid2>
      )}

      {/* Linear progress bar */}
      <Grid2 container alignItems={"center"} spacing={0.5}>
        <Grid2 size={{ xs: 2 }}>
          <Typography sx={{ fontSize: "0.85rem" }}>{value}</Typography>
        </Grid2>
        <Grid2 size={{ xs: 8 }}>
          <LinearProgress
            variant="determinate"
            value={(value / max) * 100} // Convert the value to a percentage
            sx={{ width: "100%", height: 10 }}
          />
        </Grid2>
        <Grid2 size={{ xs: 2 }}>
          <Typography sx={{ fontSize: "0.85rem", textAlign: "end" }}>{max}</Typography>
        </Grid2>
      </Grid2>

      {/* Labels below the progress bar */}
      <Grid2 container justifyContent="space-between">
        {valueLabel && (
          <Typography sx={{ fontSize: bottomLableFontSize, width: "40%" }}>{valueLabel}</Typography>
        )}
        {maxLabel && <Typography sx={{ fontSize: bottomLableFontSize }}>{maxLabel}</Typography>}
      </Grid2>
    </Box>
  );
};

interface OverflowProgressBarProps extends ProgressBarProps {
  showWarning: boolean;
}

export const LabeledOverflowLinearProgress: React.FC<OverflowProgressBarProps> = ({
  title,
  max,
  value,
  maxLabel,
  valueLabel,
  bottomLableFontSize = "0.75rem",
  showWarning
}) => {
  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      {/* Labels above the progress bar */}
      {title && (
        <Grid2 container justifyContent="center" alignItems={"center"}>
          <Typography sx={{ fontSize: "0.85rem" }}>{title}</Typography>

          {/* show warning sign */}
          {showWarning && <Warning color={"error"} fontSize="small" style={{ marginLeft: 5 }} />}
        </Grid2>
      )}

      {/* Linear progress bar */}
      <Grid2 container alignItems={"center"} spacing={0.5}>
        <Grid2 size={{ xs: 2 }}>
          <Typography sx={{ fontSize: "0.85rem" }}>{`${value.toFixed(0)}%`}</Typography>
        </Grid2>
        <Grid2 size={{ xs: 8 }}>
          <StyledOverflowLinearProgress value={value} max={max} />
        </Grid2>
        <Grid2 size={{ xs: 2 }}>
          <Typography
            sx={{ fontSize: "0.85rem", textAlign: "end" }}
          >{`${max.toFixed(0)}%`}</Typography>
        </Grid2>
      </Grid2>

      {/* Labels below the progress bar */}
      <Grid2 container justifyContent="space-between">
        {valueLabel && <Typography sx={{ fontSize: bottomLableFontSize }}>{valueLabel}</Typography>}
        {maxLabel && <Typography sx={{ fontSize: bottomLableFontSize }}>{maxLabel}</Typography>}
      </Grid2>
    </Box>
  );
};
